import React from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { breakpoints, theme } from "../../utils/styles";
import { Global } from "@emotion/core";
import { Helmet } from "react-helmet";
import ScindoTeamImage from "../../assets/scindo_team_b&w.jpg";

const Page = styled.main({
  minHeight: '100vh',
  background: theme.white,
  color: theme.black,
  margin: 0,
  padding: 0,
  position: 'relative',
});

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 32px 64px;
  
  @media (max-width: ${breakpoints.l}px) {
    padding: 100px 24px 48px;
  }
`;

const Header = styled.header`
  margin-bottom: 48px;
  text-align: left;
`;

const Title = styled.h1`
  font-size: clamp(28px, 3.5vw, 40px);
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${theme.black};
  font-family: Nunito, sans-serif;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 1rem;
  color: rgba(0,0,0,0.6);
  font-family: Nunito, sans-serif;
  margin-bottom: 32px;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 48px;
  
  @media (max-width: ${breakpoints.l}px) {
    height: 300px;
    margin-bottom: 32px;
  }
`;

const Content = styled.div`
  font-family: Nunito, sans-serif;
  line-height: 1.7;
  font-size: 1.1rem;
  color: ${theme.black};
  
  p {
    margin: 0 0 24px 0;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 48px 0 24px 0;
    color: ${theme.black};
  }
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 32px 0 16px 0;
    color: ${theme.black};
  }
  
  blockquote {
    border-left: 4px solid ${theme.primary};
    margin: 32px 0;
    padding: 16px 24px;
    background: #f9f9f9;
    font-style: italic;
  }
  
  ul, ol {
    margin: 24px 0;
    padding-left: 24px;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  .highlight {
    background: linear-gradient(120deg, ${theme.primary}20 0%, ${theme.primary}20 100%);
    padding: 2px 4px;
    border-radius: 3px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${theme.primary};
  text-decoration: none;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  margin-bottom: 32px;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:before {
    content: "←";
    font-size: 1.2rem;
  }
`;

export default function SeedRoundPost() {
  return (
    <Page>
      <Helmet>
        <title>Scindo raises £4 million to accelerate sustainable ingredients platform | Scindo</title>
        <meta name="description" content="Scindo announces £4 million seed funding round led by Kadmos Capital, with participation from Clay Capital and PINC, to accelerate AI-driven enzyme discovery platform for sustainable biotechnology." />
        
        {/* Open Graph tags for social media sharing */}
        <meta property="og:title" content="Scindo raises £4 million to accelerate sustainable ingredients platform" />
        <meta property="og:description" content="Scindo announces £4 million seed funding round led by Kadmos Capital, with participation from Clay Capital and PINC, to accelerate AI-driven enzyme discovery platform for sustainable biotechnology." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.scindo.bio/blog/scindo-raises-seed-round" />
        <meta property="og:site_name" content="Scindo" />
        <meta property="og:image" content="https://www.scindo.bio/static/scindo_team_b&w.jpg" />
        <meta property="og:image:alt" content="Scindo team members" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scindo raises £4 million to accelerate sustainable ingredients platform" />
        <meta name="twitter:description" content="Scindo announces £4 million seed funding round led by Kadmos Capital, with participation from Clay Capital and PINC." />
        <meta name="twitter:image" content="https://www.scindo.bio/static/scindo_team_b&w.jpg" />
        
        {/* Additional SEO tags */}
        <meta name="author" content="Scindo" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.scindo.bio/blog/scindo-raises-seed-round" />
      </Helmet>
      
      <Global styles={{
        'html, body': {
          background: theme.white,
          color: theme.black,
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      }} />
      
      <Container>
        <BackLink to="/media">Back to Media</BackLink>
        
        <Header>
          <Title>Scindo raises £4 million to accelerate sustainable ingredients platform</Title>
          <Meta>September 23, 2025</Meta>
        </Header>
        
        {/* Featured Image - Scindo Team */}
        <FeaturedImage 
          src={ScindoTeamImage} 
          alt="Scindo team celebrating seed round funding"
        />
        
        <Content>
          <p>
            <strong>UK startup building data-rich AI platform to discover and design next-generation enzymes secures funding to scale bio-based ingredients and sustainable chemistry solutions</strong>
          </p>
          
          <p>
            <em>Investment co-led by Kadmos Capital and Clay Capital, with participation from PINC, Paulig's venture arm</em>
          </p>
          
          <p>
            <strong>London, 23rd September 2025</strong> – <span className="highlight">Scindo, a UK-based startup developing a data-rich AI platform to discover and design next-generation enzymes</span>, has raised £4 million in funding co-led by Kadmos Capital and Clay Capital, with participation from PINC, the venture arm of international food and beverage company Paulig, and existing investors Synbioven, AgFunder, SOSV, Farvatn Venture and Savantus Ventures. The investment will accelerate Scindo's mission to enable bio-based ingredients and sustainable chemistry across multiple high-value sectors.
          </p>
          
          <p>
            Scindo's full-stack platform designs enzymes, which are naturally occurring catalytic proteins found in all living organisms, to transform renewable feedstocks into high-value, bio-based ingredients. By combining advanced AI models with proprietary, experimentally validated reaction data, and rich meta data, the platform accelerates enzyme discovery and optimisation, enabling complex reactions to be done more efficiently and sustainably. This innovation supports consistent, scalable production of natural ingredients across food, flavourings, cosmetics and other specialty chemical applications.
          </p>
          
          <h2>Strong Industry Traction</h2>
          
          <p>
            The company's technology is already attracting strong industry interest, with established partnerships with leading specialty chemical manufacturers and pilot scaling initiatives underway to bring the first products to market. This momentum underscores the rising demand for bio-based solutions that can deliver both performance and sustainability at scale.
          </p>
          
          <p>
            Founded in 2020, Scindo is entering its next phase of growth. With the new funding, the company will expand its enzyme discovery engine, scale wet-lab capabilities and strengthen its team. Recent hires include research scientists from top pharma companies and the University of Oxford, bolstering expertise in machine learning, enzyme engineering, process scaling and commercialisation.
          </p>
          
          <h2>Investor Perspectives</h2>
          
          <blockquote>
            "Scindo represents exactly the type of company we seek to back - one that combines data-driven approaches with strong technical talent to address sustainability challenges. Their use of validated experimental data to inform enzyme engineering, backed by exceptional UK academic expertise, aligns well with our investment thesis."
            <br/><br/>
            <strong>– Remy Kesrouani, Managing Partner at Kadmos Capital</strong>
          </blockquote>
          
          <blockquote>
            "The specialty chemicals industry has long sought to move away from petrochemical-derived ingredients, but existing approaches have struggled with complex natural feedstocks. Scindo's approach creates molecular craftsmen - enzymes designed for specific industrial jobs that offer cost-competitive natural alternatives and unlock previously inaccessible feedstocks, creating significant opportunities globally to end the industry's reliance on crude oil."
            <br/><br/>
            <strong>– Ali Morrow, Partner at Clay Capital</strong>
          </blockquote>
          
          <blockquote>
            "Traditional manufacturing of ingredients like flavours and fragrances still relies heavily on petroleum derivatives. However, alongside consumer expectations and policy changes, companies like Paulig also recognise its responsibility to drive the shift towards clean and sustainable alternatives."
            <br/><br/>
            <strong>– Rosemari Herrero, Senior Investment Manager at PINC</strong>
          </blockquote>
          
          <blockquote>
            "We're thrilled to have the backing of such a strong consortium of investors. This support enables us to accelerate the scale-up of our enzyme platform and start producing sustainable, high-value products that will help transform how industries approach ingredient sourcing and production."
            <br/><br/>
            <strong>– Gustaf Hemberg, Co-founder & CEO of Scindo</strong>
          </blockquote>
          
          <h2>About the Investors</h2>
          
          <h3>Kadmos Capital</h3>
          <p>
            Kadmos is a UK based VC fund, focused on early-stage deep-tech companies with hyper growth potential and positive impact including frontier technologies such as AI, Sustainability, TechBio/Longevity. 
            <br/><a href="https://www.kadmoscapital.com/" target="_blank" rel="noopener noreferrer">www.kadmoscapital.com</a>
          </p>
          
          <h3>Clay Capital</h3>
          <p>
            Clay Capital is a Singapore-based venture capital fund that backs ambitious companies developing breakthrough solutions for a healthier, safer, and more regenerative food system. As a pioneer in high-impact investment in agri-food technologies, Clay Capital is supported by major institutional investors, including government funds, supranational entities, banks, insurance companies, and prominent families from the agri-food sector.
            <br/><a href="https://www.claycapital.vc" target="_blank" rel="noopener noreferrer">www.claycapital.vc</a>
          </p>
          
          <h3>PINC & Paulig</h3>
          <p>
            PINC is Paulig's venture arm for investments in early-stage startups in and around the future of food. PINC aims to help Paulig renew itself and prosper, as well as contribute to a tastier and more sustainable planet.
            <br/><a href="https://www.pauliggroup.com/this-is-pinc" target="_blank" rel="noopener noreferrer">www.pauliggroup.com/this-is-pinc</a>
          </p>
          
          <p>
            Paulig is an international food and beverage company, growing a new, sustainable food culture – one that is good for both people and the planet. Paulig provides all things tasty: Tex Mex, Snacks, coffees, World Foods and spices. In 2024, the company's sales amounted to approximately EUR 1.2 billion. Paulig was founded in 1876 and is 100% owned by the Paulig family.
            <br/><a href="https://www.pauliggroup.com/" target="_blank" rel="noopener noreferrer">www.pauliggroup.com</a>
          </p>
          
          <h2>Media Contact</h2>
          
          <p>
            For media inquiries or interviews with representatives from Scindo, please contact:
          </p>
          
          <p>
            <strong>Lizzie Blythe</strong><br/>
            International Account Manager<br/>
            <a href="mailto:lizzie.blythe@ederalab.com">lizzie.blythe@ederalab.com</a><br/>
            +44 (0)7928 592 107
          </p>
          
          <p>
            <strong>Kurian Jacob</strong><br/>
            International Account Executive<br/>
            <a href="mailto:kurian.jacob@ederalab.com">kurian.jacob@ederalab.com</a><br/>
            +44 (0)7763 468 972
          </p>
          
          <p>
            <em>About Scindo: Scindo is a UK-based biotech startup pioneering AI-driven enzyme discovery and engineering to create sustainable alternatives to petroleum-based chemicals. Founded in 2020, the company has developed a proprietary, data-rich platform that combines experimentally validated reaction datasets with advanced AI models to discover, design and optimise novel enzymes and biotransformations to break down challenging feedstocks and convert them directly into bio-active ingredients. By transforming renewable or waste carbon into high-value products through direct, selective enzyme-driven reactions, Scindo enables cleaner, scalable solutions that advance the circular economy and the future of sustainable manufacturing.</em>
            <br/><a href="https://www.scindo.bio" target="_blank" rel="noopener noreferrer">www.scindo.bio</a>
          </p>
        </Content>
      </Container>
    </Page>
  );
}